import { ActivityPackPayloadPatch, PackService, PACK_LOCATION_MODE } from '@/types/activity-pack';
import { QUOTA_PERIOD } from '@/types/quota-period';
import { RECURRENCE_INTERVAL } from '@/types/recurrence-interval';
import { ONBOARDING_SLOT } from '@/types/onboarding-interview';

/**
 * O formato exato que o `widget-builder` do backend emite.
 *
 * O app não deriva nem formata nada: preço, duração e as linhas da grade já
 * chegam em linguagem de negócio, de uma fonte só. Reimplementar formatação
 * aqui é como a pausa do almoço some da tela em uma das telas e não na outra.
 */

export interface WeeklyGridWidgetData {
  days: {
    weekday: number;
    label: string;
    intervals: { startTime: string; endTime: string }[];
  }[];
  lines: string[];
  /** Cartão do roteiro: ele responde a pergunta em vez de só ecoá-la. */
  prompt?: boolean;
  /** Falso = o que está na tela é a semana comum do ramo, não a dele. */
  answered?: boolean;
  confirmLabel?: string;
}

export interface ServiceListWidgetData {
  services: {
    name: string;
    defaultPrice: number | null;
    priceLabel: string;
    durationMinutes: number | null;
    durationLabel: string;
    /** O valor do ramo, enquanto ele não respondeu. Nulo = já respondeu. */
    suggestedPrice?: number | null;
    suggestedPriceLabel?: string | null;
    suggestedDuration?: number | null;
    suggestedDurationLabel?: string | null;
    /** O serviço é assinatura: cobra por período, não por atendimento. */
    recurring?: boolean;
    /** Quantas sessões o plano dá por período. Nulo quando é avulso. */
    subscriptionQuota?: number | null;
    subscriptionQuotaPeriod?: QUOTA_PERIOD | null;
    /** "Assinatura: R$ 320 por mês · 4 sessões por mês". Nulo quando é avulso. */
    recurringLabel?: string | null;
    answeredPrice?: boolean;
    answeredDuration?: boolean;
    answeredRecurring?: boolean;
  }[];
  editable?: boolean;
  /** Cartão do roteiro: "Pronto" aceita as sugestões que sobraram. */
  prompt?: boolean;
  confirmLabel?: string;
  /** A cadência que "plano mensal" grava. Vem do ramo. */
  defaultInterval?: RECURRENCE_INTERVAL;
}

/** Uma pergunta de múltipla escolha do roteiro. */
export interface ChoiceWidgetData {
  slot: ONBOARDING_SLOT;
  title: string;
  hint?: string | null;
  options: {
    id: string;
    label: string;
    hint?: string;
    icon?: string;
    /** O que o toque grava. Vai inteiro no `PATCH /draft`. */
    patch?: ActivityPackPayloadPatch;
    /**
     * O ramo que o toque resolve — só a desambiguação da atividade usa. Grava
     * o `packSlug` em vez de um patch no payload.
     */
    packSlug?: string;
  }[];
}

/**
 * A lista de serviços do ramo para marcar: o passo `services` do roteiro e,
 * depois de a configuração fechar, as sugestões ("outros barbeiros também
 * fazem isso").
 */
export interface ServicePickerWidgetData {
  kind: 'setup' | 'suggestion';
  title: string;
  hint?: string | null;
  confirmLabel: string;
  skipLabel?: string | null;
  allowCustom?: boolean;
  /** A cadência que "plano mensal" grava. Vem do ramo. */
  defaultInterval?: RECURRENCE_INTERVAL;
  options: {
    name: string;
    hint?: string;
    selected: boolean;
    /** Falso = valor e tempo na tela ainda são os do ramo, não os dele. */
    answered?: boolean;
    defaultPrice?: number | null;
    priceLabel?: string;
    durationMinutes?: number | null;
    durationLabel?: string;
    /** O serviço cobra por período, não por atendimento. */
    recurring?: boolean;
    recurringLabel?: string | null;
    /** O serviço como ele entra no rascunho quando marcado. */
    patch: PackService;
  }[];
}

/** Qual parte da configuração ele quer ajustar, depois do resumo. */
export interface AdjustMenuWidgetData {
  title: string;
  options: {
    id: string;
    slot: ONBOARDING_SLOT;
    label: string;
    icon?: string;
  }[];
  doneLabel: string;
}

/** Uma resposta curta de uma linha — hoje, o nome do negócio. */
export interface TextWidgetData {
  /** O campo do rascunho que este cartão responde. */
  field: 'businessName';
  title: string;
  hint?: string | null;
  placeholder?: string;
  value?: string | null;
  confirmLabel: string;
  skipLabel?: string | null;
}

/** O endereço de quem recebe cliente, buscado por CEP. */
export interface AddressWidgetData {
  title: string;
  hint?: string | null;
  confirmLabel: string;
  skipLabel?: string | null;
  /** O que já está gravado, quando ele volta ao cartão. */
  address?: string | null;
  /** O ponto no mapa, quando ele já marcou. */
  latitude?: number | null;
  longitude?: number | null;
  /** O nome do negócio: é por ele que o mapa acha o estabelecimento. */
  businessName?: string | null;
}

export interface SubscriptionCardWidgetData {
  plans: { name: string; priceLabel: string; line: string | null }[];
}

export interface LocationWidgetData {
  mode: PACK_LOCATION_MODE;
  address?: string | null;
  label: string;
}

/**
 * Cartão de leitura genérico do assistente: um título e linhas rótulo/valor.
 * Perfil, assinatura e clientes desenham por aqui — read-only, sem toque.
 */
export interface InfoListWidgetData {
  title: string;
  /** Nome de um ícone do Ionicons. */
  icon?: string;
  rows: { label: string; value: string }[];
  footnote?: string;
}

export interface ClientPreviewWidgetData {
  /** O cartão do fecho pergunta antes de mandar para o resumo. */
  confirmLabel?: string;
  adjustLabel?: string;
  activityLabel: string | null;
  services: {
    name: string;
    priceLabel: string;
    durationLabel: string;
    /** "Plano fixo: R$ 320 por mês". Nulo quando é avulso. */
    recurrenceLabel?: string | null;
  }[];
  scheduleLines: string[];
  locationLine: string | null;
  /** "Você e mais profissionais atendendo". Nulo quando é ele sozinho. */
  teamLine?: string | null;
  /** Como ele recebe: manual ou automático. */
  paymentLine?: string | null;
}

/** Props comuns a todo widget: o cartão do último turno é o único editável. */
export interface WidgetProps<T> {
  data: T;
  /** Falso em cartão antigo do transcript — editar o de cima confunde. */
  editable?: boolean;
  /**
   * "Ajustar esta parte": só o menu de ajuste usa. Ausente no resto do
   * roteiro, onde a conversa anda sozinha.
   */
  onAdjust?: (
    input: { slot?: ONBOARDING_SLOT; done?: boolean },
    /** O que ele disse ao tocar, para a bolha dele aparecer na hora. */
    echo?: string,
  ) => void;
  /**
   * "Pronto": o prestador respondeu por toque e a conversa deve andar.
   *
   * O texto opcional é **o que ele acabou de dizer**, para a bolha dele
   * aparecer no mesmo instante do toque; quando o servidor responde, a frase
   * dele — que é a versão oficial — substitui esta.
   *
   * Ausente quando o cartão não conduz conversa nenhuma (o assistente, ou um
   * cartão antigo do transcript) — aí o botão simplesmente não aparece.
   */
  onAdvance?: (echo?: string) => void;
}
